import { Drug, DrugProductLabel, DrugSearchResult } from "common/models/types";
import axiosInstance from "./axiosInstance";
import { log } from "utils/LogUtil";
import { LogLevel } from "utils/LogUtil";

/**
 * API 응답 기본 인터페이스
 */
export interface ApiResponse<T = any> {
  /**
   * 에러 코드 (0은 성공, 이외의 값은 에러)
   */
  error: number;

  /**
   * 응답 데이터
   */
  data?: T;

  /**
   * 메타 데이터
   */
  meta?: {
    totalItems: number;
    count: number;
    hasMore: boolean;
    offset: number;
    limit: number;
  };

  /**
   * 응답 메시지 (주로 에러 발생 시 사용)
   */
  message?: string;
}

/**
 * 무한 스크롤을 위한 인터페이스
 */
export interface OffsetParams {
  offset: number;
  limit: number;
  search?: string;
  productName?: string;
  crawledFirst?: boolean;
  itemCode?: string;      // 품목기준코드로 검색
  insuranceCode?: string; // 보험코드로 검색
  specialtyOtc?: string;  // 전문/일반/HFF 구분
}

/**
 * 의약품 목록 가져오기 (오프셋 방식 적용)
 * @param params 오프셋 파라미터 (offset, limit, search)
 * @returns 무한 스크롤을 위한 의약품 목록 결과
 */
export const fetchDrugs = async (
  params: OffsetParams = { offset: 0, limit: 20 }
): Promise<ApiResponse<Drug[]>> => {
  try {
    const response = await axiosInstance.get<ApiResponse<Drug[]>>("/api/v1/drugs", {
      params,
    });
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "의약품 목록 가져오기 실패:", error);
    throw error;
  }
};

// 의약품 상세 정보 크롤링 (필요시)
export const crawlDrugInfo = async (itemCode: string): Promise<any> => {
  try {
    const response = await axiosInstance.get(`/api/v1/drugs/crawl/${itemCode}`, {
      timeout: 300000, // 5분 타임아웃 (300초)
    });
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, `의약품 상세 정보 크롤링 실패 (itemCode: ${itemCode}):`, error);
    throw error;
  }
};

/**
 * 벌크 조회 배치 크기
 * 필요시 이 값을 조정하여 성능 최적화 가능
 */
const BULK_BATCH_SIZE = 50;

// 의약품 상세 정보 크롤링 (필요시)
export const checkDrugInfo = async (itemCode: string): Promise<any> => {
  try {
    const response = await axiosInstance.get(`/api/v1/drugs/check/${itemCode}`, {
      timeout: 300000, // 5분 타임아웃 (300초)
    });
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, `의약품 상세 정보 크롤링 실패 (itemCode: ${itemCode}):`, error);
    throw error;
  }
};

/**
 * 여러 의약품의 허가사항 레코드 개수를 벌크 조회
 * - 크롤링되지 않은 항목은 count = -1 반환
 * @param itemCodes 품목기준코드 배열 (최대 50개 권장)
 * @returns itemCode -> count 매핑 객체
 */
export const checkDrugsBulk = async (
  itemCodes: string[]
): Promise<Record<string, number>> => {
  try {
    const response = await axiosInstance.post<ApiResponse<Record<string, number>>>(
      '/api/v1/drugs/check/bulk',
      { itemCodes },
      { timeout: 30000 } // 30초 타임아웃
    );

    if (response.data.error) {
      throw new Error(response.data.message || "벌크 조회 실패");
    }

    return response.data.data || {};
  } catch (error) {
    log(LogLevel.ERROR, `의약품 벌크 조회 실패 (count: ${itemCodes.length}):`, error);
    throw error;
  }
};

// 품목코드로 의약품 허가사항 가져오기
export const fetchDrugLabelByItemCode = async (itemCode: string): Promise<DrugProductLabel[]> => {
  try {
    const response = await axiosInstance.get<ApiResponse<DrugProductLabel[]>>(
      `/api/v1/drugs/product-labels/${itemCode}`
    );
    return response.data.data || [];
  } catch (error) {
    log(LogLevel.ERROR, `의약품 허가사항 가져오기 실패 (itemCode: ${itemCode}):`, error);
    throw error;
  }
};

// ID로 의약품 허가사항 가져오기
export const fetchDrugLabelById = async (id: string): Promise<DrugProductLabel[]> => {
  try {
    const response = await axiosInstance.get<ApiResponse<DrugProductLabel[]>>(
      `/api/v1/drugs/product-labels/${id}`
    );
    return response.data.data || [];
  } catch (error) {
    log(LogLevel.ERROR, `의약품 허가사항 가져오기 실패 (ID: ${id}):`, error);
    throw error;
  }
};

/**
 * 의약품 검색 API
 * @param keyword 검색어
 * @returns 검색된 의약품 목록
 */
export const searchDrug = async (
  keyword: string
): Promise<{ error?: boolean; message?: string; data?: Drug[] }> => {
  try {
    const response = await axiosInstance.get(
      `/drugs/search?keyword=${encodeURIComponent(keyword)}`
    );
    return { data: response.data };
  } catch (error: any) {
    return {
      error: true,
      message: error.response?.data?.message || "의약품 검색 중 오류가 발생했습니다.",
    };
  }
};

/**
 * 품목코드 목록으로 의약품 식별정보 조회
 * 백엔드는 요청당 최대 100개까지만 허용하므로, 100개씩 청크로 나눠 병렬 호출 후 병합한다.
 * (대용량 처방 100개+ 약물에서도 안전)
 * @param itemCodes 품목코드 배열 (개수 제한 없음 — 내부에서 100개씩 분할)
 * @returns DrugSearchResult 배열
 */
export const getPillsByItemCodes = async (
  itemCodes: string[]
): Promise<DrugSearchResult[]> => {
  if (itemCodes.length === 0) {
    return [];
  }

  // 백엔드 /drugs/pills의 요청당 상한(100개)에 맞춰 청크 분할
  const CHUNK_SIZE = 100;
  const chunks: string[][] = [];
  for (let i = 0; i < itemCodes.length; i += CHUNK_SIZE) {
    chunks.push(itemCodes.slice(i, i + CHUNK_SIZE));
  }

  try {
    const responses = await Promise.all(
      chunks.map((chunk) =>
        axiosInstance.get<ApiResponse<DrugSearchResult[]>>('/api/v1/drugs/pills', {
          params: { itemCodes: chunk.join(',') },
        })
      )
    );
    // 각 청크 결과를 병합
    return responses.flatMap((response) => response.data.data || []);
  } catch (error) {
    log(LogLevel.ERROR, `의약품 식별정보 조회 실패 (count: ${itemCodes.length}):`, error);
    throw error;
  }
};
